import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity as ActivityIcon,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  UserRound,
} from "lucide-react";
import api from "../api/api";
import { EmptyState, LoadingCard, PageHeader, PremiumCard } from "../components/ui";
import { formatDateTime } from "../utils/datetime";

function badgeTone(tone) {
  switch (tone) {
    case "danger":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "success":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "info":
      return "border-sky-200 bg-sky-50 text-sky-700";
    case "warn":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "neutral":
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function getFriendlyAction(action) {
  const a = String(action || "").trim();
  switch (a) {
    case "stock_opname.save":
      return { label: "Simpan Opname", tone: "success" };
    case "orders.checkout":
      return { label: "Checkout POS", tone: "success" };
    case "orders.refund":
      return { label: "Refund Order", tone: "warn" };
    case "admin.settings.update":
      return { label: "Update Settings", tone: "warn" };
    case "admin.staff.create":
      return { label: "Tambah Staff", tone: "success" };
    case "admin.staff.delete":
      return { label: "Hapus Staff", tone: "danger" };
    case "stock_out.create":
      return { label: "Stock Keluar", tone: "info" };
    case "stock_out.delete":
      return { label: "Hapus Stock Keluar", tone: "danger" };
    case "stock_in.create":
      return { label: "Stock Masuk", tone: "info" };
    case "bundle.create":
      return { label: "Tambah Bundle", tone: "success" };
    case "bundle.delete":
      return { label: "Hapus Bundle", tone: "danger" };
    case "bom.create":
      return { label: "Tambah BOM Rule", tone: "success" };
    case "bom.delete":
      return { label: "Hapus BOM Rule", tone: "danger" };
    case "products.create":
      return { label: "Tambah Produk", tone: "success" };
    case "products.update":
      return { label: "Update Produk", tone: "warn" };
    case "products.delete":
      return { label: "Hapus Produk", tone: "danger" };
    default:
      return { label: a ? "Aktivitas" : "-", tone: "neutral" };
  }
}

function getFriendlyEntity(entity) {
  const e = String(entity || "").trim();
  switch (e) {
    case "stock_opname":
      return { label: "Stock Opname", tone: "neutral" };
    case "stock_out":
      return { label: "Stock Keluar", tone: "neutral" };
    case "stock_in":
      return { label: "Stock Masuk", tone: "neutral" };
    case "bundles":
      return { label: "Bundle", tone: "neutral" };
    case "orders":
      return { label: "Orders", tone: "neutral" };
    case "bom_rules":
    case "bom":
      return { label: "BOM Rule", tone: "neutral" };
    case "products":
      return { label: "Produk", tone: "neutral" };
    case "settings":
      return { label: "Settings", tone: "neutral" };
    case "users":
      return { label: "Users", tone: "neutral" };
    default:
      return { label: e ? "Data" : "-", tone: "neutral" };
  }
}

function formatDescription(row) {
  const action = String(row?.action || "").trim();
  const raw = String(row?.description || "").trim();

  // orders.checkout => "Checkout order ..."
  if (action === "orders.checkout") {
    const m = raw.match(/^Checkout\s+order\s+(.+?)\s*\((\d+)\s*item,\s*(\d+)\s*qty\)\s*$/i);
    if (m) {
      const code = String(m[1] || "").trim();
      const item = String(m[2] || "").trim();
      const qty = String(m[3] || "").trim();
      return `Checkout POS berhasil untuk ${code} (${item} item, total qty ${qty}).`;
    }
    return raw || "Checkout POS diproses.";
  }

  if (action === "orders.refund") {
    const m = raw.match(/^Refund\s+order\s+(.+?)\s*\|\s*alasan:\s*(.+)$/i);
    if (m) {
      const orderNo = String(m[1] || "").trim();
      const reason = String(m[2] || "").trim();
      return `Order ${orderNo} telah di-refund. Alasan: ${reason}.`;
    }
    return raw || "Order telah di-refund.";
  }

  if (action === "admin.settings.update") {
    const m = raw.match(/^Update\s+threshold\s+stok\s+menipis:\s*(\d+)\s*->\s*(\d+)$/i);
    if (m) {
      const from = String(m[1] || "").trim();
      const to = String(m[2] || "").trim();
      return `Threshold stok menipis diperbarui dari ${from} menjadi ${to}.`;
    }
    return raw || "Settings diperbarui.";
  }

  if (action === "admin.staff.create") {
    const m = raw.match(/^Tambah\s+akun\s+staff:\s*(.+)$/i);
    if (m) {
      const username = String(m[1] || "").trim();
      return `Akun staff baru ditambahkan: ${username}.`;
    }
    return raw || "Akun staff ditambahkan.";
  }

  if (action === "admin.staff.delete") {
    const m = raw.match(/^Hapus\s+akun\s+staff:\s*(.+)$/i);
    if (m) {
      const username = String(m[1] || "").trim();
      return `Akun staff dihapus: ${username}.`;
    }
    return raw || "Akun staff dihapus.";
  }

  // stock_out.create => "Stock out KODE x10 (E-Commerce)"
  if (action === "stock_out.create") {
    const m = raw.match(/^Stock\s+out\s+(.+?)\s+x(\d+)\s*(?:\((.*?)\))?\s*$/i);
    if (m) {
      const kode = String(m[1] || "").trim();
      const qty = String(m[2] || "").trim();
      const tujuan = String(m[3] || "").trim();
      const tujuanPart = tujuan ? ` Tujuan: ${tujuan}.` : "";
      return `Stock keluar diproses untuk ${kode} sebanyak ${qty} item.${tujuanPart}`;
    }
    return raw || "Stock keluar diproses.";
  }

  // stock_in.create => "Stock in KODE x10"
  if (action === "stock_in.create") {
    const m = raw.match(/^Stock\s+in\s+(.+?)\s+x(\d+)\s*$/i);
    if (m) {
      const kode = String(m[1] || "").trim();
      const qty = String(m[2] || "").trim();
      return `Stock masuk dicatat untuk ${kode} sebanyak ${qty} item.`;
    }
    return raw || "Stock masuk dicatat.";
  }

  // stock_opname.save => "Simpan stock opname 2026-04-20 (6 item)"
  if (action === "stock_opname.save") {
    const m = raw.match(/^Simpan\s+stock\s+opname\s+(\d{4}-\d{2}-\d{2})\s*\((\d+)\s*item\)\s*$/i);
    if (m) {
      const tanggal = String(m[1] || "").trim();
      const count = String(m[2] || "").trim();
      return `Stock opname disimpan untuk tanggal ${tanggal} dengan total ${count} item.`;
    }
    return raw || "Stock opname disimpan.";
  }

  // bundle.create => "Tambah bundle: NAME (TUJUAN)"
  if (action === "bundle.create") {
    // Supports both: "Tambah bundle: Nama" and legacy "Tambah bundle: Nama (Tujuan)"
    const legacy = raw.match(/^Tambah\s+bundle:\s*(.+?)\s*\((.*?)\)\s*$/i);
    if (legacy) {
      const name = String(legacy[1] || "").trim();
      return `Bundle dibuat: ${name}.`;
    }
    const simple = raw.match(/^Tambah\s+bundle:\s*(.+?)\s*$/i);
    if (simple) {
      const name = String(simple[1] || "").trim();
      return `Bundle dibuat: ${name}.`;
    }
    return raw || "Bundle dibuat.";
  }

  // bundle.delete => "Hapus bundle id=7"
  if (action === "bundle.delete") {
    const m = raw.match(/^Hapus\s+bundle\s+id=(\d+)\s*$/i);
    if (m) {
      const id = String(m[1] || "").trim();
      return `Bundle dihapus (ID: ${id}).`;
    }
    return raw || "Bundle dihapus.";
  }

  // bom.create => "Tambah BOM: INDUK -> KOMP x2 (E-Commerce)"
  if (action === "bom.create") {
    const m = raw.match(/^Tambah\s+BOM:\s*(.+?)\s*->\s*(.+?)\s+x(\d+)\s*(?:\((.*?)\))?\s*$/i);
    if (m) {
      const induk = String(m[1] || "").trim();
      const komponen = String(m[2] || "").trim();
      const qty = String(m[3] || "").trim();
      const tujuan = String(m[4] || "").trim();
      const tujuanPart = tujuan ? ` (Tujuan: ${tujuan})` : "";
      return `BOM rule dibuat: saat stock keluar ${induk}, sistem juga mengurangi bonus ${komponen} x${qty}${tujuanPart}.`;
    }
    return raw || "BOM rule dibuat.";
  }

  // bom.delete => "Hapus BOM id=7"
  if (action === "bom.delete") {
    const m = raw.match(/^Hapus\s+BOM\s+id=(\d+)\s*$/i);
    if (m) {
      const id = String(m[1] || "").trim();
      return `BOM rule dihapus (ID: ${id}).`;
    }
    return raw || "BOM rule dihapus.";
  }

  // products.* => "Tambah/Update/Hapus produk KODE"
  if (action.startsWith("products.")) {
    const m = raw.match(/^(Tambah|Update|Hapus)\s+produk\s+(.+?)\s*$/i);
    if (m) {
      const verb = String(m[1] || "").toLowerCase();
      const kode = String(m[2] || "").trim();
      if (verb === "tambah") return `Produk baru ditambahkan: ${kode}.`;
      if (verb === "update") return `Produk diperbarui: ${kode}.`;
      if (verb === "hapus") return `Produk dihapus: ${kode}.`;
    }
  }

  // Fallback: keep original text, but avoid empty.
  return raw || "-";
}

function formatActivityTime(raw) {
  return formatDateTime(raw) || "-";
}

function MiniStat({ icon: Icon, label, value, tone = "slate" }) {
  const toneMap = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };

  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className={"flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border " + (toneMap[tone] || toneMap.slate)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-slate-500">{label}</div>
        <div className="mt-0.5 truncate text-lg font-semibold text-slate-950">{value}</div>
      </div>
    </div>
  );
}

function Activity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/activity?limit=50");
      setRows(res.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal memuat activity");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const activityStats = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const act = getFriendlyAction(row.action);
        acc.total += 1;
        if (act.tone === "success") acc.success += 1;
        if (act.tone === "danger") acc.danger += 1;
        return acc;
      },
      { total: 0, success: 0, danger: 0 }
    );
  }, [rows]);

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        eyebrow="Audit Trail"
        title="Aktivitas"
        description="Audit log: siapa melakukan apa dan kapan."
        action={
          <button onClick={fetchRows} className="btn btn-secondary w-full sm:w-auto">
            <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
            Refresh
          </button>
        }
      />

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MiniStat icon={ActivityIcon} label="Total Aktivitas" value={activityStats.total} tone="slate" />
        <MiniStat icon={CheckCircle2} label="Aksi Berhasil" value={activityStats.success} tone="emerald" />
        <MiniStat icon={AlertTriangle} label="Aksi Risiko" value={activityStats.danger} tone="rose" />
      </div>

      <div className="grid gap-3 lg:hidden">
        {loading ? (
          <LoadingCard label="Memuat aktivitas..." />
        ) : rows.length === 0 ? (
          <EmptyState icon={ActivityIcon} title="Belum ada aktivitas" description="Aktivitas operasional akan muncul di sini." />
        ) : (
          rows.map((r) => {
            const act = getFriendlyAction(r.action);
            const ent = getFriendlyEntity(r.entity);
            const desc = formatDescription(r);

            return (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-slate-500">{formatActivityTime(r.created_at)}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={"badge font-semibold " + badgeTone(act.tone)}>{act.label}</span>
                      <span className={"badge " + badgeTone(ent.tone)}>{ent.label}</span>
                    </div>
                  </div>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
                    <UserRound className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-3 text-sm text-slate-800">{desc}</div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  <span>{r.username || "-"}</span>
                  <span className="text-slate-300">|</span>
                  <span>{r.role || "-"}</span>
                  {r.entity_id != null ? (
                    <>
                      <span className="text-slate-300">|</span>
                      <span>Ref: #{r.entity_id}</span>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <PremiumCard className="hidden p-0 lg:block">
      <div className="table-shell rounded-none border-0 shadow-none">
        <table className="min-w-full text-sm">
          <thead className="table-head text-left text-slate-600">
            <tr>
              <th className="px-4 py-3 font-semibold">Waktu</th>
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Action</th>
              <th className="px-4 py-3 font-semibold">Entity</th>
              <th className="px-4 py-3 font-semibold">Deskripsi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td className="px-4 py-4 text-slate-600" colSpan={5}>
                  <LoadingCard label="Memuat aktivitas..." />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-4 py-4 text-slate-600" colSpan={5}>
                  <EmptyState icon={ActivityIcon} title="Belum ada aktivitas" description="Aktivitas operasional akan muncul di sini." />
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                (() => {
                  const act = getFriendlyAction(r.action);
                  const ent = getFriendlyEntity(r.entity);
                  const desc = formatDescription(r);

                  return (
                <tr key={r.id} className="table-row">
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-700">
                    {formatActivityTime(r.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-900">{r.username || "-"}</div>
                    <div className="text-xs text-slate-500">{r.role || "-"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={"badge font-semibold " + badgeTone(act.tone)}>{act.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className={"badge " + badgeTone(ent.tone)}>{ent.label}</span>
                    </div>
                    {r.entity_id != null ? <div className="mt-1 text-xs text-slate-500">Ref: #{r.entity_id}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-800">{desc}</td>
                </tr>
                  );
                })()
              ))
            )}
          </tbody>
        </table>
      </div>
      </PremiumCard>
    </div>
  );
}

export default Activity;
